/* Center canvas: inline SVG (via ref.innerHTML) with hand-rolled
   pan / pinch-zoom and click-to-select. Pan/zoom live in refs and
   write style.transform directly — no React re-render per pointermove.
   Ported 1:1 from the vanilla setSvg / bindViewer / handleCanvasClick. */

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { pathKind, resolvePath, selectionTitle } from './utils';
import { MessageSquarePlus, MapPin } from 'lucide-react';
import { anchorQuote, anchorRevision, normalizedPoint, useCommentAnchors } from './CommentAnchors';
import type { CommentAnchor } from './api';
import InlineTextEditor from './InlineTextEditor';
import { bindInlineText, InlineTextTarget, resolveInlineTextValue } from './inlineText';

export interface SvgCanvasHandle {
  setSvg: (text: string, preserveView: boolean) => void;
  getBlockAnchor: (path: string) => CommentAnchor | null;
  // Editing affordances (DESIGN5 §FEUX2): smoothly frame an element,
  // pulse it, and drive an external hover glow from the Structure tree.
  zoomToPath: (path: string) => void;
  flashPath: (path: string) => void;
  setHoverPath: (path: string | null) => void;
  // Re-write the current transform after the viewport is resized (e.g.
  // the sources panel collapse widens this column). Deliberately does
  // NOT refit — it must not yank the operator's zoom. (DESIGN9 §3)
  reapplyView: () => void;
}

interface Props {
  documentId?: string;
  workingRef: React.MutableRefObject<any>;
  selPath: string | null;
  onSelect: (path: string | null) => void;
  canEditText?: boolean;
  onApplyText?: (target: InlineTextTarget, value: string) => Promise<void | boolean>;
}

const SvgCanvas = forwardRef<SvgCanvasHandle, Props>(function SvgCanvas(
  { workingRef, selPath, onSelect, documentId, canEditText = false, onApplyText },
  ref,
) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const zoomLabelRef = useRef<HTMLSpanElement>(null);
  const [empty, setEmpty] = useState(true);
  const [editing, setEditing] = useState<InlineTextTarget | null>(null);
  const editingRef = useRef<InlineTextTarget | null>(null);
  const textBindings = useRef(new Map<Element, InlineTextTarget>());
  const commentTapAt = useRef(-Infinity);
  const editProps = useRef({ canEditText, onApplyText });
  editProps.current = { canEditText, onApplyText };
  const closeText = () => { editingRef.current = null; setEditing(null); };
  const beginText = (target: EventTarget | null) => {
    if (!editProps.current.canEditText || !editProps.current.onApplyText || editingRef.current || commentRef.current?.picking || performance.now() - commentTapAt.current < 400) return false;
    const node = (target as Element | null)?.closest?.('text');
    const binding = node && textBindings.current.get(node);
    if (!binding || !stageRef.current?.contains(node!) || resolveInlineTextValue(workingRef.current, binding.path) !== binding.value) return false;
    cancelAnim(); setHoverPath(null);
    const vp = viewportRef.current;
    if (vp && vp.clientWidth < 600) {
      const rect = binding.nodes[0].getBoundingClientRect();
      const frame = vp.getBoundingClientRect();
      const fontSize = parseFloat(window.getComputedStyle(binding.nodes[0]).fontSize || '18');
      if (fontSize * zoom.current < 16 && rect.width > 0) {
        const x = (rect.left - frame.left - panX.current) / zoom.current;
        const y = (rect.top - frame.top - panY.current) / zoom.current;
        const anchor = binding.nodes[0].getAttribute('text-anchor');
        const center = (rect.left + rect.width / 2 - frame.left - panX.current) / zoom.current;
        zoom.current = Math.min(8, 16 / fontSize);
        panX.current = anchor === 'middle' ? frame.width / 2 - center * zoom.current : 24 - x * zoom.current;
        panY.current = Math.max(88, Math.min(rect.top - frame.top, frame.height / 3)) - y * zoom.current;
        applyTransform();
      }
    }
    if (vp) {
      const frame = vp.getBoundingClientRect();
      const rects = binding.nodes.map(n => n.getBoundingClientRect());
      const top = Math.min(...rects.map(r => r.top)), bottom = Math.max(...rects.map(r => r.bottom));
      if (bottom > top) {
        const visibleTop = Math.max(frame.top + 80, Math.min(top, frame.bottom - (bottom - top) - 24));
        panY.current += visibleTop - top;
        applyTransform();
      }
    }
    editingRef.current = binding; setEditing(binding);
    return true;
  };
  const comments = useCommentAnchors();
  const commentRef = useRef(comments);
  commentRef.current = comments;
  const pinLayerRef = useRef<HTMLDivElement>(null);
  const pinPositionsRef = useRef<Array<{ node: HTMLButtonElement; x: number; y: number }>>([]);
  const svgContentRevision = useRef('');
  const currentDocumentRevision = () => anchorRevision({ spec: workingRef.current, svg: svgContentRevision.current });
  const documentIdRef = useRef(documentId);
  documentIdRef.current = documentId;
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const findPath = (path: string) => Array.from(stageRef.current?.querySelectorAll<SVGGraphicsElement>('[data-path]') || []).find(node => node.dataset.path === path);
  const makeBlockAnchor = (path: string): CommentAnchor | null => {
    const node = findPath(path);
    if (!documentIdRef.current) return null;
    // Older approved posters can be a single raster image in an SVG shell.
    // Their Structure tree still has a real spec path, so anchor that content
    // instead of silently removing the comment action from every row.
    const value = resolvePath(workingRef.current, path)?.value;
    if (!node && value == null) return null;
    const source = node?.textContent || (value == null ? '' : [
      value.title, value.subtitle, value.intro, value.desc, value.bullets,
      value.paras, value.items, value.tail,
      Array.isArray(value) ? value : null,
    ].flat(2).filter(part => typeof part === 'string').join(' '));
    const quote = anchorQuote(source || selectionTitle(pathKind(path), value));
    return { kind: 'block', document_id: documentIdRef.current, revision: currentDocumentRevision(), path, label: quote.slice(0, 100) || 'Architecture block', quote };
  };
  const positionPins = () => {
    pinPositionsRef.current.forEach(pin => {
      pin.node.style.left = (panX.current + pin.x * svgW.current * zoom.current) + 'px';
      pin.node.style.top = (panY.current + pin.y * svgH.current * zoom.current) + 'px';
    });
  };
  const refreshPins = () => {
    const layer = pinLayerRef.current;
    if (!layer) return;
    layer.replaceChildren(); pinPositionsRef.current = [];
    const context = commentRef.current;
    if (!context || context.stage !== 'architecture') return;
    const currentRevision = currentDocumentRevision();
    for (const thread of context.threads) {
      const anchor = thread.anchor;
      if (thread.parent_id || thread.resolved_at || thread.deleted || !anchor || anchor.document_id !== documentIdRef.current) continue;
      let x = anchor.x, y = anchor.y;
      if (anchor.kind === 'block') {
        const node = anchor.path ? findPath(anchor.path) : null;
        if (!node || anchorQuote(node.textContent || '') !== anchorQuote(anchor.quote || '')) continue;
        try { const box = node.getBBox(); x = (box.x + box.width) / svgW.current; y = box.y / svgH.current; } catch { continue; }
      } else if (anchor.revision !== currentRevision) continue;
      if (x == null || y == null) continue;
      const button = document.createElement('button'); button.type = 'button'; button.className = 'arch-svg-comment-pin';
      button.textContent = '•••'; button.setAttribute('aria-label', 'Open comments on ' + anchor.label); button.title = anchor.label;
      button.addEventListener('pointerdown', event => event.stopPropagation());
      button.addEventListener('click', event => { event.stopPropagation(); commentRef.current?.openThread(thread.id); });
      layer.append(button); pinPositionsRef.current.push({ node: button, x, y });
    }
    positionPins();
  };

  // view state (kept in refs so pointermove never triggers a re-render)
  const zoom = useRef(1);
  const panX = useRef(24);
  const panY = useRef(24);
  const svgW = useRef(1880);
  const svgH = useRef(4000);

  // in-flight zoom animation (rAF id) and the current external hover
  const animRef = useRef<number | null>(null);
  const hoverPathRef = useRef<string | null>(null);

  // latest selection, readable from imperative callbacks
  const selPathRef = useRef<string | null>(selPath);
  useEffect(() => {
    selPathRef.current = selPath;
  }, [selPath]);

  const applyTransform = () => {
    if (stageRef.current) {
      stageRef.current.style.transform =
        'translate(' +
        panX.current +
        'px, ' +
        panY.current +
        'px) scale(' +
        zoom.current +
        ')';
    }
    positionPins();
    if (zoomLabelRef.current) {
      zoomLabelRef.current.textContent = Math.round(zoom.current * 100) + '%';
    }
  };

  const fitWidth = () => {
    const vp = viewportRef.current;
    if (!vp) return;
    const rect = vp.getBoundingClientRect();
    // On the editor's very first commit the grid may not have laid out
    // yet, so the viewport measures ~0 wide and the fit collapses to the
    // 2% zoom floor. Retry on the next frame until layout is real.
    if (rect.width < 100) {
      window.requestAnimationFrame(fitWidth);
      return;
    }
    const z = Math.max(0.02, Math.min(8, (rect.width - 48) / svgW.current));
    zoom.current = z;
    panX.current = (rect.width - svgW.current * z) / 2;
    panY.current = 24;
    applyTransform();
  };

  const zoomAt = (factor: number, cx: number, cy: number) => {
    const z = Math.max(0.02, Math.min(8, zoom.current * factor));
    const f = z / zoom.current;
    panX.current = cx - (cx - panX.current) * f;
    panY.current = cy - (cy - panY.current) * f;
    zoom.current = z;
    applyTransform();
  };

  const outlineFor = (path: string | null) => {
    const stage = stageRef.current;
    if (!stage) return;
    stage
      .querySelectorAll('.arch-selected')
      .forEach((n) => n.classList.remove('arch-selected'));
    if (!path) return;
    const g = stage.querySelector('[data-path="' + path + '"]');
    if (g) g.classList.add('arch-selected');
  };

  const cancelAnim = () => {
    if (animRef.current != null) {
      cancelAnimationFrame(animRef.current);
      animRef.current = null;
    }
  };

  // Smoothly lerp zoom + pan to a target over ~240ms (easeInOutQuad),
  // writing the same style.transform the rest of the viewer uses.
  const animateTo = (tz: number, tpx: number, tpy: number) => {
    cancelAnim();
    const sz = zoom.current;
    const spx = panX.current;
    const spy = panY.current;
    const dur = 240;
    const start = performance.now();
    const ease = (t: number) =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const e = ease(t);
      zoom.current = sz + (tz - sz) * e;
      panX.current = spx + (tpx - spx) * e;
      panY.current = spy + (tpy - spy) * e;
      applyTransform();
      if (t < 1) animRef.current = requestAnimationFrame(step);
      else animRef.current = null;
    };
    animRef.current = requestAnimationFrame(step);
  };

  // Frame the element named by `path`: fit its bbox to ~70% of the
  // viewport (never past 110%) and centre it, animated. getBBox() is in
  // SVG user units, which equal stage-local px (svg width == viewBox
  // width, origin 0) — the same assumption fitWidth already relies on.
  const zoomToPath = (path: string) => {
    const stage = stageRef.current;
    const vp = viewportRef.current;
    if (!stage || !vp) return;
    const g = stage.querySelector(
      '[data-path="' + path + '"]',
    ) as SVGGraphicsElement | null;
    if (!g || typeof g.getBBox !== 'function') return;
    let bb: DOMRect;
    try {
      bb = g.getBBox();
    } catch (_) {
      return;
    }
    if (!bb || !bb.width || !bb.height) return;
    const rect = vp.getBoundingClientRect();
    const vw = rect.width;
    const vh = rect.height;
    const tz = Math.max(
      0.02,
      Math.min(8, (vw * 0.7) / bb.width, (vh * 0.7) / bb.height, 1.1),
    );
    const cx = bb.x + bb.width / 2;
    const cy = bb.y + bb.height / 2;
    animateTo(tz, vw / 2 - cx * tz, vh / 2 - cy * tz);
  };

  // Pulse an element (CSS .arch-flash, ~900ms); restart if re-flashed.
  const flashPath = (path: string) => {
    const stage = stageRef.current;
    if (!stage) return;
    const g = stage.querySelector('[data-path="' + path + '"]');
    if (!g) return;
    g.classList.remove('arch-flash');
    // force reflow so the animation restarts even on the same element
    void (g as SVGGraphicsElement).getBoundingClientRect();
    g.classList.add('arch-flash');
    window.setTimeout(() => g.classList.remove('arch-flash'), 950);
  };

  // Soft hover glow — one element at a time, weaker than the selection.
  const applyHover = (path: string | null) => {
    const stage = stageRef.current;
    if (!stage) return;
    stage
      .querySelectorAll('.arch-hover')
      .forEach((n) => n.classList.remove('arch-hover'));
    if (!path) return;
    const g = stage.querySelector('[data-path="' + path + '"]');
    if (g) g.classList.add('arch-hover');
  };

  const setHoverPath = (path: string | null) => {
    if (hoverPathRef.current === path) return;
    hoverPathRef.current = path;
    applyHover(path);
  };

  const setSvg = (svgText: string, preserveView: boolean) => {
    const stage = stageRef.current;
    if (!stage) return;
    cancelAnim(); // any in-flight zoom targets a now-stale element
    hoverPathRef.current = null; // hovered group is gone with the re-inject
    svgContentRevision.current = anchorRevision(svgText);
    stage.innerHTML = svgText;
    textBindings.current = bindInlineText(stage, workingRef.current);
    closeText();
    const svg = stage.querySelector('svg');
    if (!svg) return;
    let w = 0;
    let h = 0;
    try {
      if (svg.viewBox && svg.viewBox.baseVal && svg.viewBox.baseVal.width) {
        w = svg.viewBox.baseVal.width;
        h = svg.viewBox.baseVal.height;
      }
    } catch (_) {
      /* ignore */
    }
    if (!w) w = parseFloat(svg.getAttribute('width') || '') || 1880;
    if (!h) h = parseFloat(svg.getAttribute('height') || '') || 4000;
    svgW.current = w;
    svgH.current = h;
    svg.style.width = w + 'px';
    svg.style.height = h + 'px';
    stage.style.width = w + 'px';
    stage.style.height = h + 'px';
    setEmpty(false);
    if (preserveView) applyTransform();
    else fitWidth();
    // re-apply the selection outline after the DOM was replaced
    outlineFor(selPathRef.current);
    refreshPins();
  };

  useImperativeHandle(ref, () => ({
    setSvg,
    getBlockAnchor: makeBlockAnchor,
    zoomToPath,
    flashPath,
    setHoverPath,
    reapplyView: applyTransform,
  }));

  // A local edit can change the working ref before a new SVG arrives; hide stale pins immediately.
  useEffect(() => { refreshPins(); });
  const registerComments = comments?.register;
  useEffect(() => {
    if (!registerComments || !documentId) return;
    return registerComments('architecture', documentId, anchor => {
      if (anchor.kind === 'block') {
        const current = anchor.path ? makeBlockAnchor(anchor.path) : null;
        if (!current) return 'missing';
        if (current.quote !== anchor.quote) return 'changed';
        if (findPath(anchor.path!)) {
          zoomToPath(anchor.path!); flashPath(anchor.path!);
        } else {
          // A raster poster has no semantic SVG node to frame. Reveal the
          // corresponding editable Structure block instead.
          onSelectRef.current(anchor.path!);
        }
        return 'found';
      }
      if (anchor.revision !== currentDocumentRevision()) return 'changed';
      if (anchor.x == null || anchor.y == null || !viewportRef.current) return 'missing';
      const rect = viewportRef.current.getBoundingClientRect();
      const targetZoom = Math.max(zoom.current, Math.min(1, rect.width / (svgW.current * .6)));
      animateTo(targetZoom, rect.width / 2 - anchor.x * svgW.current * targetZoom, rect.height / 2 - anchor.y * svgH.current * targetZoom);
      return 'found';
    });
    // Imperative canvas helpers read refs, never capture a document draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerComments, documentId]);

  // initial zoom label (the span is left empty so React never resets
  // the value we write imperatively)
  useEffect(() => {
    if (zoomLabelRef.current && !zoomLabelRef.current.textContent) {
      zoomLabelRef.current.textContent = Math.round(zoom.current * 100) + '%';
    }
  }, []);

  // keep the outline in sync when the selection changes without a
  // re-injection (e.g. clicking a different element, adopt re-select)
  useEffect(() => {
    outlineFor(selPath);
  }, [selPath]);

  const handleCanvasClick = (target: EventTarget | null) => {
    const working = workingRef.current;
    if (!working) return;
    let g: Element | null =
      target && (target as Element).closest
        ? (target as Element).closest('[data-path]')
        : null;
    while (g) {
      const p = g.getAttribute('data-path');
      if (p && pathKind(p)) {
        const r = resolvePath(working, p);
        if (r && r.value !== undefined && r.value !== null) {
          onSelect(p);
          return;
        }
      }
      g = g.parentElement ? g.parentElement.closest('[data-path]') : null;
    }
    onSelect(null);
  };

  // Pointer/wheel binding — done imperatively so wheel can be
  // non-passive (preventDefault) and to match the SPA exactly.
  // StrictMode-safe: everything is cleaned up on unmount.
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;

    const onWheel = (e: WheelEvent) => {
      if (editingRef.current) return;
      e.preventDefault();
      cancelAnim(); // user takes over from any programmatic zoom
      const rect = vp.getBoundingClientRect();
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      zoomAt(factor, e.clientX - rect.left, e.clientY - rect.top);
    };

    const pointers = new Map<number, { x: number; y: number }>();
    let downX = 0;
    let downY = 0;
    let lastX = 0;
    let lastY = 0;
    let dragging = false;
    let downTarget: EventTarget | null = null;
    let pinching = false;
    let pinchDist = 0;
    let pinchMidX = 0;
    let pinchMidY = 0;
    let pendingClick: ReturnType<typeof setTimeout> | undefined;
    let lastTap: { node: Element; time: number; x: number; y: number } | null = null;
    const clearTap = () => { if (pendingClick) clearTimeout(pendingClick); pendingClick = undefined; lastTap = null; };

    const onPointerDown = (e: PointerEvent) => {
      if (editingRef.current) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      cancelAnim(); // a drag/pinch overrides any programmatic zoom
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try {
        vp.setPointerCapture(e.pointerId);
      } catch (_) {
        /* ignore */
      }
      if (pointers.size === 1) {
        dragging = false;
        downX = lastX = e.clientX;
        downY = lastY = e.clientY;
        downTarget = e.target;
      } else if (pointers.size === 2) {
        clearTap();
        pinching = true;
        dragging = true; // suppress the click-select on release
        downTarget = null;
        vp.classList.add('panning');
        const rect = vp.getBoundingClientRect();
        const [a, b] = Array.from(pointers.values());
        pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
        pinchMidX = (a.x + b.x) / 2 - rect.left;
        pinchMidY = (a.y + b.y) / 2 - rect.top;
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pinching) {
        if (pointers.size < 2) return;
        const rect = vp.getBoundingClientRect();
        const [a, b] = Array.from(pointers.values());
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const midX = (a.x + b.x) / 2 - rect.left;
        const midY = (a.y + b.y) / 2 - rect.top;
        panX.current += midX - pinchMidX;
        panY.current += midY - pinchMidY;
        if (pinchDist > 0 && dist > 0) zoomAt(dist / pinchDist, midX, midY);
        else applyTransform();
        pinchDist = dist;
        pinchMidX = midX;
        pinchMidY = midY;
        return;
      }

      if (pointers.size !== 1) return;
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      if (!dragging && Math.hypot(e.clientX - downX, e.clientY - downY) > 4) {
        clearTap();
        dragging = true;
        vp.classList.add('panning');
      }
      if (dragging) {
        panX.current += dx;
        panY.current += dy;
        applyTransform();
      }
      lastX = e.clientX;
      lastY = e.clientY;
    };

    const finish = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.delete(e.pointerId);
      if (pinching) {
        if (pointers.size < 2) {
          pinching = false;
          pinchDist = 0;
          if (pointers.size === 1) {
            const p = Array.from(pointers.values())[0];
            lastX = p.x;
            lastY = p.y;
            dragging = true;
          } else {
            vp.classList.remove('panning');
          }
        }
        return;
      }
      if (pointers.size === 0) {
        vp.classList.remove('panning');
        if (!dragging && downTarget && e.type !== 'pointercancel') {
          const context = commentRef.current;
          if (context?.picking && context.stage === 'architecture' && documentIdRef.current) {
            clearTap(); commentTapAt.current = performance.now();
            const path = (downTarget as Element).closest?.('[data-path]')?.getAttribute('data-path');
            const block = path ? makeBlockAnchor(path) : null;
            const svg = stageRef.current?.querySelector('svg');
            const point = svg ? normalizedPoint(e.clientX, e.clientY, svg.getBoundingClientRect()) : null;
            if (block && !context.pointOnly) context.start(block);
            else if (point) context.start({ kind: 'point', document_id: documentIdRef.current, revision: currentDocumentRevision(), label: 'Architecture point', ...point });
          } else {
            const node = (downTarget as Element).closest?.('text');
            if (node && textBindings.current.has(node) && editProps.current.canEditText) {
              const now = performance.now();
              if (lastTap?.node === node && now - lastTap.time < 350 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 24) {
                clearTap(); beginText(node);
              } else {
                clearTap();
                lastTap = { node, time: now, x: e.clientX, y: e.clientY };
                const selectedTarget = downTarget;
                pendingClick = setTimeout(() => { clearTap(); if (!editingRef.current) handleCanvasClick(selectedTarget); }, 350);
              }
            } else { clearTap(); handleCanvasClick(downTarget); }
          }
        }
        if (e.type === 'pointercancel') clearTap();
        downTarget = null;
        dragging = false;
      }
    };

    // Hover glow — throttled with rAF, only while idle (no drag/pinch).
    // Uses elementFromPoint so the topmost data-path under the cursor
    // wins; walks up to the closest SELECTABLE ancestor for consistency
    // with click-select. Pan/zoom logic above is untouched.
    let hoverRaf = 0;
    let hoverX = 0;
    let hoverY = 0;
    const onHoverMove = (e: PointerEvent) => {
      if (editingRef.current || dragging || pinching || pointers.size > 0) return;
      hoverX = e.clientX;
      hoverY = e.clientY;
      if (hoverRaf) return;
      hoverRaf = window.requestAnimationFrame(() => {
        hoverRaf = 0;
        const el = document.elementFromPoint(hoverX, hoverY);
        let g: Element | null =
          el && (el as Element).closest ? (el as Element).closest('[data-path]') : null;
        let path: string | null = null;
        while (g) {
          const p = g.getAttribute('data-path');
          if (p && pathKind(p)) {
            path = p;
            break;
          }
          g = g.parentElement ? g.parentElement.closest('[data-path]') : null;
        }
        setHoverPath(path);
      });
    };
    const onHoverLeave = () => {
      if (hoverRaf) {
        cancelAnimationFrame(hoverRaf);
        hoverRaf = 0;
      }
      setHoverPath(null);
    };
    const onDoubleClick = (event: MouseEvent) => {
      if (beginText(event.target)) { clearTap(); event.preventDefault(); }
    };

    vp.addEventListener('wheel', onWheel, { passive: false });
    vp.addEventListener('pointerdown', onPointerDown);
    vp.addEventListener('pointermove', onPointerMove);
    vp.addEventListener('pointermove', onHoverMove);
    vp.addEventListener('pointerleave', onHoverLeave);
    vp.addEventListener('pointerup', finish);
    vp.addEventListener('pointercancel', finish);
    vp.addEventListener('dblclick', onDoubleClick);
    return () => {
      clearTap();
      cancelAnim();
      if (hoverRaf) cancelAnimationFrame(hoverRaf);
      vp.removeEventListener('wheel', onWheel);
      vp.removeEventListener('pointerdown', onPointerDown);
      vp.removeEventListener('pointermove', onPointerMove);
      vp.removeEventListener('pointermove', onHoverMove);
      vp.removeEventListener('pointerleave', onHoverLeave);
      vp.removeEventListener('pointerup', finish);
      vp.removeEventListener('pointercancel', finish);
      vp.removeEventListener('dblclick', onDoubleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zoomBtn = (factor: number) => {
    const vp = viewportRef.current;
    if (!vp) return;
    const r = vp.getBoundingClientRect();
    zoomAt(factor, r.width / 2, r.height / 2);
  };

  return (
    <div className={'arch-canvas-wrap' + (editing ? ' is-editing-text' : '')}>
      <div className="arch-canvas-toolbar">
        <button
          className="arch-btn arch-btn-ghost arch-btn-sm"
          title="Zoom out"
          aria-label="Zoom out"
          onClick={() => zoomBtn(1 / 1.25)}
        >
          −
        </button>
        <span ref={zoomLabelRef} className="arch-zoom-level" />
        <button
          className="arch-btn arch-btn-ghost arch-btn-sm"
          title="Zoom in"
          aria-label="Zoom in"
          onClick={() => zoomBtn(1.25)}
        >
          +
        </button>
        <button
          className="arch-btn arch-btn-ghost arch-btn-sm"
          title="Fit width"
          aria-label="Fit width"
          onClick={fitWidth}
        >
          ⤢ fit width
        </button>
        {!empty && comments?.enabled ? <div className="arch-canvas-comment-action">
          {selPath ? <button type="button" className="arch-btn arch-btn-ghost arch-btn-sm" title="Comment on selected block" aria-label="Comment on selected block" onClick={() => { const anchor = makeBlockAnchor(selPath); if (anchor) comments.start(anchor); }}><MessageSquarePlus size={17} /></button> : null}
          <button type="button" className="arch-btn arch-btn-ghost arch-btn-sm" title="Place a comment on the architecture" aria-label="Place a comment on the architecture" aria-pressed={comments.picking} onClick={() => comments.setPicking(!comments.picking, true)}><MapPin size={17} /></button>
        </div> : null}
      </div>
      <div ref={viewportRef} className={'arch-svg-viewport' + (comments?.picking ? ' is-comment-picking' : '') + (canEditText ? ' can-edit-text' : '')}>
        <div ref={stageRef} className="arch-svg-stage" />
        <div ref={pinLayerRef} className="arch-svg-comment-pins" aria-label="Architecture comments" />
        <div className={'arch-canvas-empty' + (empty ? '' : ' arch-hidden')}>
          <div className="arch-canvas-empty-icon">◧</div>
          <div>Upload notes and press "Generate"</div>
        </div>
      </div>
      {!empty && canEditText && !editing && !comments?.picking && textBindings.current.size > 0 ? <div className="arch-inline-hint">Double-click or double-tap text to edit</div> : null}
      {editing && onApplyText && viewportRef.current ? <InlineTextEditor target={editing} viewport={viewportRef.current} onApply={onApplyText} onClose={closeText} /> : null}
    </div>
  );
});

export default SvgCanvas;
